import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { callAPIWithoutAuth } from '../../utils/apiUtils';
import { apiUrls } from '../../utils/apiUrls';

export const fetchTestimonials = createAsyncThunk(
    'testimonial/fetchTestimonials',
    async (limit = 10, { rejectWithValue }) => {
        try {
            const params = { limit };
            const response = await callAPIWithoutAuth(
                apiUrls.testimonialListing,
                params,
                "GET",
                {}
            );

            if (response?.data?.success) {
                return response.data.data;
            } else {
                return rejectWithValue(response?.data?.message || 'Failed to fetch testimonials');
            }
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Network error');
        }
    }
);

export const fetchHomeTestimonials = createAsyncThunk(
    'testimonial/fetchHomeTestimonials',
    async (limit = 5, { rejectWithValue }) => {
        try {
            const params = { limit, featured: true };
            const response = await callAPIWithoutAuth(
                apiUrls.homeTestimonials,
                params,
                "GET",
                {}
            );

            if (response?.data?.success) {
                return response.data.data;
            } else {
                return rejectWithValue(response?.data?.message || 'Failed to fetch home testimonials');
            }
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Network error');
        }
    }
);

export const submitTestimonial = createAsyncThunk(
    'testimonial/submitTestimonial',
    async (testimonialData, { rejectWithValue }) => {
        try {
            const response = await callAPIWithoutAuth(
                apiUrls.submitTestimonial,
                testimonialData,
                "POST",
                {}
            );

            if (response?.data?.success) {
                return response.data.data;
            } else {
                return rejectWithValue(response?.data?.message || 'Failed to submit testimonial');
            }
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Network error');
        }
    }
);

const testimonialSlice = createSlice({
    name: 'testimonial',
    initialState: {
        testimonials: [],
        homeTestimonials: [],
        loadingT: false,
        homeLoading: false,
        submitLoading: false,
        error: null,
        submitSuccess: false
    },
    reducers: {
        clearTestimonialError: (state) => {
            state.error = null;
        },
        clearTestimonials: (state) => {
            state.testimonials = [];
            state.homeTestimonials = [];
        },
        resetSubmitStatus: (state) => {
            state.submitSuccess = false;
            state.error = null;
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchTestimonials.pending, (state) => {
                state.loadingT = true;
                state.error = null;
            })
            .addCase(fetchTestimonials.fulfilled, (state, action) => {
                state.loadingT = false;
                state.testimonials = action.payload;
            })
            .addCase(fetchTestimonials.rejected, (state, action) => {
                state.loadingT = false;
                state.error = action.payload;
            })
            
            .addCase(fetchHomeTestimonials.pending, (state) => {
                state.homeLoading = true;
                state.error = null;
            })
            .addCase(fetchHomeTestimonials.fulfilled, (state, action) => {
                state.homeLoading = false;
                state.homeTestimonials = action.payload;
            })
            .addCase(fetchHomeTestimonials.rejected, (state, action) => {
                state.homeLoading = false;
                state.error = action.payload;
            })
            
            .addCase(submitTestimonial.pending, (state) => {
                state.submitLoading = true;
                state.submitSuccess = false;
                state.error = null;
            })
            .addCase(submitTestimonial.fulfilled, (state, action) => {
                state.submitLoading = false;
                state.submitSuccess = true;
                state.testimonials = [action.payload, ...state.testimonials];
            })
            .addCase(submitTestimonial.rejected, (state, action) => {
                state.submitLoading = false;
                state.submitSuccess = false;
                state.error = action.payload;
            });
    }
});

export const { clearTestimonialError, clearTestimonials, resetSubmitStatus } = testimonialSlice.actions;
export default testimonialSlice.reducer;